import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { KeyRound, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SetupPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('No se encontró el token de configuración');
      setValidating(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-setup-token/${token}`);
        setTokenValid(true);
        setUserInfo(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Token inválido o expirado');
      } finally {
        setValidating(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/setup-password`, {
        token,
        password
      });
      
      setSuccess(true);
      toast.success('¡Contraseña creada correctamente!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear la contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#C6FD38] mx-auto mb-4" />
          <p className="text-white/60">Verificando enlace...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Enlace inválido</h1>
            <p className="text-white/60 mb-6">{error}</p>
            <Link to="/login">
              <Button className="w-full bg-[#C6FD38] hover:bg-[#b8ef2a] text-[#0A0A12]">
                Ir al inicio de sesión
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state - For Founders, show info, not login
  if (success) {
    const isFounder = userInfo?.is_founder || userInfo?.subscription_status === 'trialing';
    
    return (
      <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#C6FD38]/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#C6FD38]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {isFounder ? '🎉 ¡Ya eres Founder!' : '¡Cuenta configurada!'}
            </h1>
            <p className="text-white/60 mb-4">
              {isFounder 
                ? 'Tu cuenta está lista. Tu acceso se activará el 31 de julio de 2026.'
                : 'Tu contraseña ha sido creada correctamente.'
              }
            </p>
            <p className="text-[#C6FD38] font-medium mb-6">{userInfo?.email}</p>
            
            {isFounder ? (
              <>
                <Link to="/">
                  <Button className="w-full bg-[#C6FD38] hover:bg-[#b8ef2a] text-[#0A0A12]">
                    Volver al inicio
                  </Button>
                </Link>
              </>
            ) : (
              <Link to="/login">
                <Button className="w-full bg-[#C6FD38] hover:bg-[#b8ef2a] text-[#0A0A12]">
                  Iniciar sesión
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Setup form
  return (
    <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C6FD38]/20 to-indigo-500/20 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-[#C6FD38]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">¡Bienvenido a FASCINAI!</h1>
            <p className="text-white/60">
              Configura tu contraseña para acceder a tu cuenta
            </p>
          </div>

          {/* User info */}
          {userInfo && (
            <div className="bg-white/5 rounded-lg p-4 mb-6 text-center">
              <p className="text-white/60 text-sm">Tu cuenta</p>
              <p className="text-white font-medium">{userInfo.email}</p>
              {userInfo.name && (
                <p className="text-[#C6FD38] text-sm">{userInfo.name}</p>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Crear contraseña
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  required
                  minLength={6}
                  data-testid="setup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Confirmar contraseña
              </label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite tu contraseña"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                required
                minLength={6}
                data-testid="setup-password-confirm"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || password.length < 6 || password !== confirmPassword}
              className="w-full bg-[#C6FD38] hover:bg-[#b8ef2a] text-[#0A0A12] h-12 mt-6 font-semibold"
              data-testid="setup-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando contraseña...
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Crear contraseña
                </>
              )}
            </Button>
          </form>

          {/* Password requirements */}
          <div className="mt-6 p-4 bg-white/5 rounded-lg">
            <p className="text-xs text-white/40 mb-2">Tu contraseña debe tener:</p>
            <ul className="text-xs text-white/60 space-y-1">
              <li className={password.length >= 6 ? 'text-[#C6FD38]' : ''}>
                • Mínimo 6 caracteres {password.length >= 6 && '✓'}
              </li>
              <li className={password === confirmPassword && password.length > 0 ? 'text-[#C6FD38]' : ''}>
                • Contraseñas coinciden {password === confirmPassword && password.length > 0 && '✓'}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupPassword;
